import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Pie, Line } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title } from "chart.js";
import {
  FaDatabase,
  FaCubes,
  FaExchangeAlt,
  FaCoins,
  FaAward,
  FaSignInAlt,
  FaSignOutAlt,
} from "react-icons/fa";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title);

const About = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("https://explorer.menelik.cc:3300/api/table-records");
        if (!response.ok) {
          throw new Error("Failed to fetch data");
        }
        const data = await response.json();

        const chartData = {
          labels: data.transactions_last_14_days.points.map((point) =>
            new Date(point.date).toLocaleDateString()
          ),
          datasets: [
            {
              label: "Transactions in the Last 14 Days",
              data: data.transactions_last_14_days.points.map((point) =>
                parseInt(point.count, 10)
              ),
              fill: false,
              backgroundColor: "rgba(75,192,192,0.4)",
              borderColor: "rgba(75,192,192,1)",
            },
          ],
        };

        setStats({ ...data, chartData });
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <p className="text-center text-yellow-400">Loading data...</p>;
  }

  if (error) {
    return <p className="text-center text-red-500">Error: {error}</p>;
  }

  const rewardData = {
    labels: ["Miners (PoW)", "Smartnodes (PoS)", "Development"],
    datasets: [
      {
        data: [75, 15, 5],
        backgroundColor: ["#FFD700", "#FF4500", "#808080"],
        hoverBackgroundColor: ["#FFEC8B", "#FF6347", "#A9A9A9"],
      },
    ],
  };

  return (
    <section id="about" className="py-16 px-6 bg-cover bg-center relative">
      <div className="absolute inset-0 bg-black bg-opacity-90"></div>

      <div className="relative max-w-7xl mx-auto text-center mb-12">
        <motion.h2
          className="text-4xl font-bold text-yellow-400 mb-6 border-b-4 border-red-600 inline-block"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Menelik Blockchain
        </motion.h2>
        <p className="text-gray-300 text-lg">
          Menelik is designed to deliver a superior experience in both{" "}
          <strong className="text-yellow-400">mining and staking</strong>, offering a robust and efficient ecosystem.
        </p>
      </div>

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-black bg-opacity-50 p-6 rounded-lg shadow-lg border-l-4 border-red-600"
        >
          <h3 className="font-semibold text-xl text-yellow-400 flex items-center">
            <FaCubes className="mr-2 text-red-500" /> Main Specifications
          </h3>
          <ul className="list-disc ml-4 mt-2 text-gray-300">
            <li><strong>Name:</strong> Menelik</li>
            <li><strong>Symbol:</strong> MENEL</li>
            <li><strong>Algorithm:</strong> GhostRider</li>
            <li><strong>Max Supply:</strong> 21,000,000,000</li>
            <li><strong>Block Time:</strong> 2 Minutes</li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 0 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-black bg-opacity-50 p-6 rounded-lg shadow-lg border-l-4 border-yellow-500"
        >
          <h3 className="font-semibold text-xl text-red-500 flex items-center">
            <FaAward className="mr-2 text-yellow-400" /> Reward Distribution
          </h3>
          <ul className="list-disc ml-4 mt-2 text-gray-300">
            <li><strong>Block Reward:</strong> 5,000 MENEL</li>
            <li><strong>Smartnodes (PoS):</strong> 15%</li>
            <li><strong>Miners (PoW):</strong> 75%</li>
            <li><strong>Development:</strong> 5% (250 MENEL)</li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-black bg-opacity-50 p-6 rounded-lg shadow-lg border-l-4 border-red-600"
        >
          <h3 className="font-semibold text-xl text-yellow-400 mb-4 flex items-center justify-center">
            <FaCoins className="mr-2 text-yellow-500" /> Reward Breakdown
          </h3>
          <div className="w-60 mx-auto">
            <Pie data={rewardData} />
          </div>
        </motion.div>
      </div>

      <div className="relative max-w-7xl mx-auto mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-black bg-opacity-50 p-6 rounded-lg shadow-lg border-l-4 border-red-600"
        >
          <h3 className="font-semibold text-xl mb-2 text-yellow-400 flex items-center">
            <FaDatabase className="mr-2 text-yellow-500" /> Blockchain Statistics
          </h3>
          <ul className="text-gray-300 space-y-1">
          <li><FaExchangeAlt className="inline mr-2 text-blue-400" /> Addresses: {stats.addresses.toLocaleString()}</li>
            <li><FaCubes className="inline mr-2 text-green-400" /> Blocks: {stats.blocks.toLocaleString()}</li>
            <li><FaCoins className="inline mr-2 text-yellow-500" /> Transactions: {stats.transactions.toLocaleString()}</li>
            <li><FaSignInAlt className="inline mr-2 text-purple-400" /> Transaction Entries: {stats.transaction_inputs.toLocaleString()}</li>
            <li><FaSignOutAlt className="inline mr-2 text-red-400" /> Transaction Outputs: {stats.transaction_outputs.toLocaleString()}</li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-black bg-opacity-50 p-6 rounded-lg shadow-lg border-l-4 border-yellow-500"
        >
          <Line data={stats.chartData} />
        </motion.div>
      </div>
    </section>
  );
};

export default About;
